import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import isEmpty from "lodash/isEmpty";
import isArray from "lodash/isArray";
import { patrolTemplateService, patrolService, restClient } from "client-app-core";

import { IconButton, SvgIcon, Button, InputAdornment, FormControlLabel, Checkbox } from "@mui/material";
import { mdiCollapseAll, mdiExpandAll } from "@mdi/js";
import { default as Expand } from "@mui/icons-material/ZoomOutMap";
import SearchIcon from "@mui/icons-material/Search";

import { getDir } from "orion-components/i18n/Config/selector";
import { Translate, getTranslation } from "orion-components/i18n";
import { Dialog as CBDialog } from "orion-components/CBComponents";
import { openDialog, closeDialog, setLocalAppState } from "orion-components/AppState/Actions";
import CustomizedTextField from "orion-components/SharedComponents/CustomizedTextField/CustomizedTextField";
import { getWidgetState } from "orion-components/Profiles/Selectors";
import PatrolCard from "./components/PatrolCard";
import DeviceTree from "orion-components/DeviceManager/DeviceTree/DeviceTree";
import { canManageByApplication } from "orion-components/Profiles/Selectors";
import { selectedContextSelector } from "orion-components/ContextPanel/Selectors";
import { unsubscribeFromFeed, startEventPatrolStream } from "orion-components/ContextualData/Actions";

const formControlLabelStyle = {
	margin: "unset",
	"& .MuiFormControlLabel-label": {
		fontSize: "10px",
		color: "#FFFFFF"
	}
};

const PatrolsWidget = ({ id, selected, expanded, widgetsExpandable, selectWidget, contextId, readOnly }) => {
	const dispatch = useDispatch();

	const enabled = useSelector((state) => expanded || getWidgetState(state)(id, "enabled"));
	const dialog = useSelector((state) => state.appState?.dialog?.openDialog || "");
	const dir = useSelector((state) => getDir(state));
	const canManage = useSelector((state) => canManageByApplication(state, "events-app", "manage"));
	const canView = useSelector(
		(state) => !readOnly && canManageByApplication(state, "events-app", "canView", "config")
	);
	const showCompletedPatrols = useSelector((state) => state.appState.persisted?.showCompletedPatrols);
	const patrols = useSelector((state) => state.appState.persisted?.patrols);
	const context = useSelector((state) => selectedContextSelector(state));
	const event = context?.entity;
	const patrolInstances = context?.patrols;
	const isTemplate = event?.isTemplate;

	const [collapsed, setCollapsed] = useState(false);
	const [patrolName, setPatrolName] = useState("");
	const [checkpointDialog, setCheckpointDialog] = useState({});
	const [adhocCheckpoint, setAdhocCheckpoint] = useState("");
	const [deviceCheckpoint, setDeviceCheckpoint] = useState(null);
	const [resources, setResources] = useState([]);
	const [search, setSearch] = useState("");
	const [patrolToDelete, setPatrolToDelete] = useState({});
	const [timezone, setTimezone] = useState(null);

	useEffect(() => {
		patrolService.getAppConfig("timezone", (err, data) => {
			if (err) {
				console.error("Failed to fetch config:", err);
				setTimezone("America/New_York");
			} else {
				setTimezone(data?.result || "America/New_York");
			}
		});
	}, []);

	useEffect(() => {
		loadPatrols();
		if (!isTemplate) {
			dispatch(startEventPatrolStream(contextId, "profile"));
			loadResources();
		}

		return () => {
			dispatch(unsubscribeFromFeed(contextId, "patrols", "profile"));
			dispatch(setLocalAppState("patrols", []));
		};
	}, []);

	useEffect(() => {
		loadPatrols();
	}, [selected]);

	useEffect(() => {
		if (patrolInstances?.length) dispatch(setLocalAppState("patrols", patrolInstances));
	}, [patrolInstances]);

	const loadPatrols = async () => {
		const service = isTemplate ? patrolTemplateService : patrolService;
		await service.getByEventId(contextId, (err, data) => {
			if (err) {
				console.log(err);
			} else {
				dispatch(setLocalAppState("patrols", data));
			}
		});
	};
	const handleExpand = () => {
		dispatch(selectWidget("patrols"));
	};

	const loadResources = async () => {
		await restClient.exec_get("/access-management-app/api/cardHolders?isSecurityStaff=true", (err, response) => {
			if (err) console.log(err);
			if (response && isArray(response)) {
				setResources(response);
			}
		});
	};

	const openAddPatrolDialog = () => {
		dispatch(openDialog("addPatrolDialog"));
	};

	const closeAddPatrolDialog = () => {
		setPatrolName("");
		dispatch(closeDialog("addPatrolDialog"));
	};

	const closeCheckpointDialog = () => {
		setAdhocCheckpoint("");
		setDeviceCheckpoint(null);
		setCheckpointDialog({});
		setSearch("");
	};

	const handlePatrolNameChange = (e) => {
		setPatrolName(e.target.value);
	};

	const createPatrolTemplate = async () => {
		await patrolTemplateService.create({ name: patrolName, eventId: contextId }, (err, res) => {
			if (err) {
				console.log("Error creating patrol template:", err);
			} else if (res?.id) {
				dispatch(setLocalAppState("patrols", [...patrols, res]));
			}
		});
		closeAddPatrolDialog();
	};

	const addCheckpoint = async () => {
		const data = { type: checkpointDialog?.type };
		if (checkpointDialog?.type === "adhoc") data["name"] = adhocCheckpoint;
		if (checkpointDialog?.type === "device") data["deviceId"] = deviceCheckpoint;

		await patrolTemplateService.addCheckpoint(checkpointDialog?.id, data, (err, res) => {
			if (err) {
				console.log("Error occurred while adding checkpoint:", err);
			} else if (res?.id) {
				const update = patrols.map((item) => {
					if (item.id === checkpointDialog?.id) {
						item["checkpoints"] = item.checkpoints ? [...item.checkpoints, res] : [res];
						return item;
					} else {
						return item;
					}
				});
				dispatch(setLocalAppState("patrols", update));
			}
		});
		closeCheckpointDialog();
	};

	const removeCheckpoint = async (patrolTemplateId, checkpointId) => {
		await patrolTemplateService.deleteCheckpoint(patrolTemplateId, checkpointId, (err, res) => {
			if (err) {
				console.error("Error occurred while deleting checkpoint:", err);
			} else if (res) {
				const update = patrols.map((item) => {
					if (item.id === patrolTemplateId) {
						item["checkpoints"] = item.checkpoints?.filter((checkpoint) => checkpoint.id !== checkpointId);
						return item;
					} else {
						return item;
					}
				});
				dispatch(setLocalAppState("patrols", update));
			}
		});
	};

	const updatePatrolTemplate = async (patrolTemplateId, data) => {
		await patrolTemplateService.update(patrolTemplateId, data, (err, res) => {
			if (err) {
				console.error("Error updating patrol template:", err);
			} else if (res?.id) {
				const update = patrols.map((item) => (item.id === patrolTemplateId ? res : item));
				dispatch(setLocalAppState("patrols", update));
			}
		});
	};

	const updatePatrolStatus = async (status, id) => {
		const callback = (err) => {
			if (err) {
				console.error("Error updating patrol:", err);
			}
		};

		if (status === "start") {
			await patrolService.startPatrol(id, callback);
		} else if (status === "complete") {
			await patrolService.completePatrol(id, callback);
		}
	};

	const completeCheckpoint = async (id, checkpointId) => {
		await patrolService.completeCheckpoint(id, checkpointId, (err) => {
			if (err) {
				console.error("Error updating patrol:", err);
			}
		});
	};

	const assignResource = async (id, resourceId) => {
		await patrolService.assignResource(id, resourceId, (err) => {
			if (err) {
				console.error("Error assigning resource to patrol:", err);
			}
		});
	};

	const openDeletePatrolDialog = (patrol) => {
		setPatrolToDelete(patrol);
	};

	const closeDeletePatrolDialog = () => {
		setPatrolToDelete({});
	};

	const deletePatrol = async () => {
		await patrolTemplateService.delete(patrolToDelete?.id, (err, res) => {
			if (err) {
				console.log(err);
			} else if (res) {
				const update = patrols.filter((item) => item.id !== patrolToDelete?.id);
				dispatch(setLocalAppState("patrols", update));
			}
		});
		closeDeletePatrolDialog();
	};

	const styles = {
		svgIcon: {
			width: "20px",
			height: "20px",
			color: "#FCFDFD",
			cursor: "pointer",
			...(dir === "rtl" && { marginLeft: "17px" }),
			...(dir === "ltr" && { marginRight: "17px" })
		},
		widgetExpandButton: {
			width: "auto",
			...(dir === "rtl" && { paddingLeft: 0 }),
			...(dir === "ltr" && { paddingRight: 0 })
		},
		ButtonStyles: {
			fontSize: 10,
			padding: "unset",
			marginBottom: 15
		},
		wrapper: {
			direction: dir,
			...(expanded && patrols.length && { background: "#494D53" })
		},
		searchIcon: { width: 20, height: 20, color: "#B5B9BE" }
	};

	const selectedPatrol = patrols?.find((patrol) => patrol.id === checkpointDialog?.id);
	const deviceCheckpoints =
		selectedPatrol?.checkpoints
			?.filter((checkpoint) => checkpoint.type === "device")
			?.map((checkpoint) => checkpoint.deviceId) || [];

	return selected || !enabled ? (
		<div />
	) : (
		<>
			<div className="widget-wrapper" style={styles.wrapper}>
				{!expanded ? (
					<div className="widget-header" style={{ marginTop: -8, flexDirection: "unset" }}>
						<SvgIcon
							style={styles.svgIcon}
							onClick={() => {
								// setChildKey(childKey + 1);
								setCollapsed(!collapsed);
							}}
						>
							<path d={collapsed ? mdiExpandAll : mdiCollapseAll} />
						</SvgIcon>
						<div className="cb-font-b2">
							<Translate value="global.profiles.widgets.patrols.main.title" />
						</div>
						{!isTemplate && <div>
							<FormControlLabel
								control={
									<Checkbox
										edge="start"
										checked={showCompletedPatrols}
										tabIndex={-1}
										disableRipple
										onChange={(e) => dispatch(setLocalAppState("showCompletedPatrols", e.target.checked))}
										sx={{
											margin: "0 5px",
											padding: "unset",
											"& .MuiSvgIcon-root": {
												width: "18px",
												height: "18px"
											}
										}}
									/>
								}
								sx={formControlLabelStyle}
								label={getTranslation(`global.profiles.widgets.patrols.main.showCompleted`)}
							/>
						</div>}
						<div className="widget-header-buttons">
							{widgetsExpandable && !expanded ? (
								<div className="widget-expand-button">
									<IconButton style={styles.widgetExpandButton} onClick={handleExpand}>
										<Expand />
									</IconButton>
								</div>
							) : null}
						</div>
					</div>
				) : null}
				{!collapsed && (
					<div>
						{canManage && isTemplate && !expanded && (
							<div className="widget-content">
								<Button
									style={styles.ButtonStyles}
									variant="text"
									color="primary"
									onClick={openAddPatrolDialog}
								>
									<Translate value="global.profiles.widgets.patrols.main.addPatrol" />
								</Button>
							</div>
						)}
						{timezone !== null && patrols?.length
							? patrols
								.filter((patrol) => showCompletedPatrols ? true : patrol.status !== "COMPLETED")
								.sort((a, b) => new Date(a.start) - new Date(b.start))
								.map((patrol, index) => (
									<PatrolCard
										key={index}
										patrol={patrol}
										dir={dir}
										setCheckpointDialog={setCheckpointDialog}
										removeCheckpoint={removeCheckpoint}
										adminRole={canManage}
										operatorRole={!canManage && canView}
										widgetExpanded={expanded}
										isTemplate={isTemplate}
										updatePatrolTemplate={updatePatrolTemplate}
										updatePatrolStatus={updatePatrolStatus}
										assignResource={assignResource}
										completeCheckpoint={completeCheckpoint}
										resources={resources}
										openDeletePatrolDialog={openDeletePatrolDialog}
										timezone={timezone}
									/>
								))
							: null}
						<CBDialog
							open={dialog === "addPatrolDialog"}
							title={getTranslation("global.profiles.widgets.patrols.main.dialog.addPatrol")}
							textContent={getTranslation("global.profiles.widgets.patrols.main.dialog.textContent")}
							confirm={{
								label: getTranslation("global.profiles.widgets.patrols.main.dialog.add"),
								action: createPatrolTemplate,
								disabled: isEmpty(patrolName.trim())
							}}
							abort={{
								label: getTranslation("global.profiles.widgets.patrols.main.dialog.cancel"),
								action: closeAddPatrolDialog
							}}
							options={{
								onClose: closeAddPatrolDialog,
								maxWidth: "md",
								fullWidth: true
							}}
							dir={dir}
							paperPropStyles={{ maxWidth: 650 }}
						>
							<div style={{ marginTop: 30 }}>
								<CustomizedTextField
									id="patrolName"
									variant="outlined"
									fullWidth
									value={patrolName}
									placeholder={getTranslation(
										"global.profiles.widgets.patrols.main.dialog.enterName"
									)}
									autoFocus={true}
									onChange={handlePatrolNameChange}
									customWidth={"100%"}
									inputProps={{ style: { fontSize: 12 } }}
								/>
							</div>
						</CBDialog>
						<CBDialog
							open={checkpointDialog?.type === "adhoc"}
							title={getTranslation("global.profiles.widgets.patrols.main.dialog.addAdhocCheckpoint")}
							textContent={getTranslation("global.profiles.widgets.patrols.main.dialog.provideName")}
							confirm={{
								label: getTranslation("global.profiles.widgets.patrols.main.dialog.add"),
								action: addCheckpoint,
								disabled: isEmpty(adhocCheckpoint.trim())
							}}
							abort={{
								label: getTranslation("global.profiles.widgets.patrols.main.dialog.cancel"),
								action: closeCheckpointDialog
							}}
							options={{
								onClose: closeCheckpointDialog,
								maxWidth: "md",
								fullWidth: true
							}}
							dir={dir}
							paperPropStyles={{ maxWidth: 650 }}
						>
							<div style={{ marginTop: 30 }}>
								<CustomizedTextField
									id="adhocCheckpoint"
									variant="outlined"
									fullWidth
									value={adhocCheckpoint}
									placeholder={getTranslation(
										"global.profiles.widgets.patrols.main.dialog.enterName"
									)}
									autoFocus={true}
									onChange={(e) => setAdhocCheckpoint(e.target.value)}
									customWidth={"100%"}
									inputProps={{ style: { fontSize: 12 } }}
								/>
							</div>
						</CBDialog>
						<CBDialog
							open={checkpointDialog?.type === "device"}
							title={getTranslation("global.profiles.widgets.patrols.main.dialog.addDeviceCheckpoint")}
							textContent={getTranslation("global.profiles.widgets.patrols.main.dialog.selectDevice")}
							confirm={{
								label: getTranslation("global.profiles.widgets.patrols.main.dialog.add"),
								action: addCheckpoint,
								disabled: isEmpty(deviceCheckpoint)
							}}
							abort={{
								label: getTranslation("global.profiles.widgets.patrols.main.dialog.cancel"),
								action: closeCheckpointDialog
							}}
							options={{
								onClose: closeCheckpointDialog,
								maxWidth: "md",
								fullWidth: true
							}}
							dir={dir}
							paperPropStyles={{ maxWidth: 650 }}
						>
							<div style={{ marginTop: 30 }}>
								<CustomizedTextField
									variant="outlined"
									placeholder={getTranslation("global.deviceManager.deviceConfigSidePanel.search")}
									value={search}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon sx={styles.searchIcon} />
											</InputAdornment>
										)
									}}
									onChange={(e) => {
										setSearch(e.target.value);
									}}
								/>
								<DeviceTree
									readOnly={true}
									allowDeviceSelection={true}
									getSelectedNodes={(device) => setDeviceCheckpoint(device)}
									ignoreEmptyGroups={true}
									entitySelectionFilters={{
										ignoreCameras: true,
										ignoreCompositeDevices: true,
										entitiesToExclude: deviceCheckpoints
									}}
									search={search}
									autoExpandMatchingNodes={true}
									customStyles={{ marginTop: 30 }}
									addUngroupedDevicesToRoot={true}
								/>
							</div>
						</CBDialog>
						<CBDialog
							open={patrolToDelete?.id}
							title={getTranslation("global.profiles.widgets.patrols.main.dialog.deletePatrol")}
							textContent={getTranslation(
								"global.profiles.widgets.patrols.main.dialog.deletePatrolText",
								patrolToDelete?.name
							)}
							confirm={{
								label: getTranslation("global.profiles.widgets.patrols.main.dialog.confirm"),
								action: deletePatrol,
								disabled: isEmpty(patrolToDelete?.id)
							}}
							abort={{
								label: getTranslation("global.profiles.widgets.patrols.main.dialog.cancel"),
								action: closeDeletePatrolDialog
							}}
							options={{
								onClose: closeDeletePatrolDialog,
								maxWidth: "md",
								fullWidth: true
							}}
							dir={dir}
							paperPropStyles={{ maxWidth: 650 }}
						></CBDialog>
					</div>
				)}
			</div>
			{canManage && isTemplate && expanded && (
				<div className="widget-content" style={{ marginTop: 15 }}>
					<Button style={styles.ButtonStyles} variant="text" color="primary" onClick={openAddPatrolDialog}>
						<Translate value="global.profiles.widgets.patrols.main.addPatrol" />
					</Button>
				</div>
			)}
		</>
	);
};

export default PatrolsWidget;
